import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import Loader from 'components/partials/loader/loader';
import CriticalElementChart from 'containers/user/goals/goals-monitoring/monitoring-chart/critical-element-chart';
import Button from 'components/partials/button/button';

import { getClientCriticalElementById } from 'api/generated/critical-elements/critical-elements';
import { fetchMonitoringSettings } from 'api/monitoring';
import { getCriticalElementMonitoring } from 'api/generated/critical-element-monitoring/critical-element-monitoring';

import { captureInsightScreenshot } from 'helpers/utils/insight-screenshot';

import {
  DATE_FORMAT,
  STAGE_CONFIG,
  MONITORING_CHART_MAX_WIDTH,
} from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/constants';

import Icons from 'assets/icons';

interface BlockData {
  key: string;
  value: unknown;
  previous: unknown;
  percentageChange: unknown;
  cohort: unknown;
  cohortPercentageChange: unknown;
}

interface YearLabels {
  prev: string;
  curr: string;
  cohort: string;
}

interface CriticalElementMonitoringTabProps {
  criticalElementId: string | number | undefined;
  clientId: string | number | undefined;
  userId: string | undefined;
  entryTerm?: string | number;
}

const CriticalElementMonitoringTab = ({
  criticalElementId,
  clientId,
  userId,
  entryTerm,
}: CriticalElementMonitoringTabProps): React.JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const [blocks, setBlocks] = useState<BlockData[]>([]);
  const [stageLabel, setStageLabel] = useState<string | null>(null);
  const [criticalElementSummary, setCriticalElementSummary] =
    useState<string>('');
  const [currentYearLabels, setCurrentYearLabels] = useState<YearLabels>({
    prev: 'Prev',
    curr: 'Curr',
    cohort: '',
  });

  useEffect(() => {
    const fetchMonitoring = async (): Promise<void> => {
      if (!criticalElementId || !clientId || !userId) {
        setLoading(false);

        return;
      }

      try {
        try {
          const ceDetails = await getClientCriticalElementById(
            userId,
            Number(clientId),
            Number(criticalElementId)
          );

          setCriticalElementSummary(ceDetails?.summary || '');
        } catch (e) {
          console.error('Error fetching critical element summary', e);
          setCriticalElementSummary('');
        }

        const [settings, ceData] = await Promise.all([
          fetchMonitoringSettings(userId, clientId),
          getCriticalElementMonitoring(userId, Number(criticalElementId)),
        ]);

        const monitoringSettings = settings?.monitoring;

        if (!monitoringSettings || !ceData) {
          setBlocks([]);
          setLoading(false);

          return;
        }

        const { inquiriesToApps, applicationsToAdmits, admitsToEnrollment } =
          monitoringSettings;
        const today = dayjs();
        const dateITA = dayjs(inquiriesToApps, DATE_FORMAT);
        const dateATA = dayjs(applicationsToAdmits, DATE_FORMAT);
        const dateATE = dayjs(admitsToEnrollment, DATE_FORMAT);

        let stageKey: string | null = null;

        if (today.isBefore(dateITA)) {
          stageKey = null;
        } else if (today.isBefore(dateATA)) {
          stageKey = 'inquiriesToApps';
        } else if (today.isBefore(dateATE)) {
          stageKey = 'applicationsToAdmits';
        } else {
          stageKey = 'admitsToEnrollment';
        }

        if (!stageKey) {
          setBlocks([]);
          setLoading(false);

          return;
        }

        const buildBlocks = (
          data: Record<string, unknown>,
          fields: string[]
        ): BlockData[] =>
          fields.map((key) => ({
            key,
            value: data?.[key],
            previous: data?.[`${key}Previous`],
            percentageChange: data?.[`${key}PercentageChange`],
            cohort: data?.[`${key}Cohort`],
            cohortPercentageChange: data?.[`${key}CohortPercentageChange`],
          }));

        const fields = (
          STAGE_CONFIG as unknown as Record<string, { fields: string[] }>
        )[stageKey].fields;

        const STAGE_LABELS: Record<string, string> = {
          inquiriesToApps: 'Inquiries to Apps',
          applicationsToAdmits: 'Applications to Admits',
          admitsToEnrollment: 'Admits to Enrollment',
        };

        setStageLabel(STAGE_LABELS[stageKey] || null);

        setBlocks(buildBlocks(ceData as Record<string, unknown>, fields));
        setCurrentYearLabels({
          prev: String(monitoringSettings?.comparisonYears?.[0] ?? 'Prev'),
          curr: String(monitoringSettings?.comparisonYears?.[1] ?? 'Curr'),
          cohort: monitoringSettings?.cohort ?? '',
        });
      } catch (e) {
        console.error('Error fetching CE monitoring', e);
      } finally {
        setLoading(false);
      }
    };

    fetchMonitoring();
  }, [criticalElementId, clientId, userId]);

  if (loading)
    return (
      <Loader
        loading
        overlay="white"
        customClass="opacity-75"
        fullScreen={false}
      />
    );

  if (!blocks.length) return <div>No monitoring data</div>;

  return (
    <div style={{ maxWidth: MONITORING_CHART_MAX_WIDTH }} className="mt-5">
      <div className="flex justify-between items-center mb-2">
        {stageLabel && (
          <div className="text-gray-600 text-sm flex gap-4">
            <span>
              Current funnel stage:{' '}
              <span className="font-semibold text-gray-600">{stageLabel}</span>
            </span>
            {entryTerm && (
              <span>
                Entry term:{' '}
                <span className="font-semibold text-gray-600">{entryTerm}</span>
              </span>
            )}
          </div>
        )}
        <Button
          variant="reserve-outlined"
          className="shrink-0"
          iconBefore={<Icons.Insight aria-label="InsightIcon" />}
          size="sm"
          onClick={() =>
            captureInsightScreenshot(
              document.getElementById(`ce-chart-${criticalElementId}`),
              {
                title: '',
                description: '',
                summary: criticalElementSummary || stageLabel,
                criticalElement: criticalElementSummary || stageLabel,
                criticalElementId,
                clientId,
                relatedGoals: [],
                source: 'monitoring',
              } as never
            )
          }
        >
          <span>Add insight</span>
        </Button>
      </div>
      <CriticalElementChart
        chartId={`ce-chart-${criticalElementId}`}
        blocks={blocks as never}
        previousYear={currentYearLabels.prev}
        currentYear={currentYearLabels.curr}
        cohortName={currentYearLabels.cohort}
      />
    </div>
  );
};

export default CriticalElementMonitoringTab;
